import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useAuth } from '../../context/AuthContext';
import {
  formatPhoneInputMask,
  phoneDigitsForApi,
} from '../../utils/phoneInputMask';
import { SERVICE_AREA_STATE_CODES } from '../../utils/serviceAreaStates';
import { businessService } from '../../services/api/businessService';
import { accountService } from '../../services/api/accountService';
import { teamContactMailtoHref } from '../../utils/teamContact';
import {
  isEnrollmentAccountConflict,
  enrollmentAccountConflictTitle,
} from '../../utils/providerSignupEnrollment';
import {
  getPasswordStrength,
  getPasswordValidationError,
} from '../../utils/passwordRules';
import {
  getSignupEmailError,
  getSignupPhoneError,
} from '../../utils/signupFieldValidation';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import {
  normalizeBusinessAddress,
  normalizeBusinessEmail,
  normalizeBusinessFormFieldValue,
  normalizeBusinessWebsiteUrl,
} from '../../utils/normalizeBusinessAddress';
import { toPersonNameCase } from '../../utils/normalizePersonName';
import { PRO_OFFER_SUMMARY } from '../../constants/proPricing';
import {
  SignupPageWrapper,
  HeroBanner,
  HeroTitle,
  HeroSubtitle,
  ProgressBarTrack,
  ProgressBarFill,
  StepperContainer,
  StepIndicator,
  StepCircle,
  StepLabelText,
  StepConnector,
  FormCard,
  StepTitle,
  StepSubtitle,
  FieldGroup,
  FieldRow,
  Label,
  Input,
  Textarea,
  Select,
  ErrorText,
  AvailabilityGrid,
  AvailabilityChip,
  AvailabilityChipIcon,
  AvailabilityChipLabel,
  AvailabilityChipDesc,
  SummaryGrid,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  ReviewTimelinesList,
  ReviewTimelineRow,
  ReviewTimelineCheckboxCell,
  ReviewTimelineText,
  ReviewTimelineTitle,
  ReviewTimelineDesc,
  AgreementSignatureCard,
  AgreementSignatureHeading,
  AgreementSignatureHint,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
  SuccessWrapper,
  SuccessIcon,
  SuccessTitle,
  SuccessMessage,
  TrialBadge,
  FinePrint,
  PasswordWrapper,
  PasswordToggle,
  PasswordStrength,
  PasswordStrengthBar,
  PasswordStrengthLabel,
  ConfirmPasswordMatch,
  SectionDivider,
  SectionLabel,
  LoadingOverlay,
  LoadingTitle,
  LoadingSteps,
  LoadingStep,
  LoadingStepIcon,
  ModalBackdrop,
  ModalCard,
  ModalIcon,
  ModalTitle,
  ModalBody,
  ModalActions,
  ModalPrimaryButton,
  ModalSecondaryButton,
} from './ProviderSignup.styled';

/* ═══════════════════════════════════════════════════════════════════════════
   STREAMLINED PROVIDER SIGNUP
   ─────────────────────────────────────────────────────────────────────────
   4-step flow (guest):
     Step 1 → Business info (name, address, contact, availability)
     Step 2 → User / account info (name, email/username, password, phone, DOB)
     Step 3 → Review & submit (creates the account + business)
     Step 4 → Partnership Agreement (mandatory sign-off before continuing)
   Logged-in users (already have an account) skip Step 2: Business → Review →
   Agreement.
   ═══════════════════════════════════════════════════════════════════════════ */

const PARTNERSHIP_AGREEMENT_PDF_FILE = 'Business Partnership Agreement.pdf';

/** Mirrors PartnershipAgreementDashboard's PDF src builder so the embedded viewer matches. */
function buildPartnershipAgreementPdfSrc() {
  const base = process.env.PUBLIC_URL || '';
  const path = `${base}/legal/${encodeURIComponent(PARTNERSHIP_AGREEMENT_PDF_FILE).replace(/%2F/g, '/')}`;
  return `${path}#navpanes=0&zoom=100`;
}

const AVAILABILITY_OPTIONS = [
  {
    key: 'asap',
    label: 'Emergency / ASAP',
    icon: '🚨',
    desc: "Urgent jobs that can't wait",
  },
  {
    key: 'today',
    label: 'Same Day',
    icon: '📅',
    desc: 'Request comes in, you handle it today',
  },
  {
    key: 'few-days',
    label: 'Within a Few Days',
    icon: '🗓️',
    desc: 'Flexible, scheduled within the week',
  },
];

const STEPS = [
  { key: 'business', label: 'Business' },
  { key: 'account', label: 'Your Info' },
  { key: 'review', label: 'Review' },
  { key: 'agreement', label: 'Agreement' },
];

function collectBusinessStepErrors(form) {
  const errs = {};
  if (!form.businessName.trim()) errs.businessName = 'Business name is required';
  if (!form.addressLine1.trim()) errs.addressLine1 = 'Address is required';
  if (!form.city.trim()) errs.city = 'City is required';
  if (!form.state) errs.state = 'State is required';
  if (!form.zip.trim()) errs.zip = 'ZIP code is required';
  const bizEmailErr = getSignupEmailError(form.businessEmail, {
    requiredLabel: 'Business email is required',
  });
  if (bizEmailErr) errs.businessEmail = bizEmailErr;
  const bizPhoneErr = getSignupPhoneError(form.businessPhone, {
    requiredLabel: 'Business phone is required',
  });
  if (bizPhoneErr) errs.businessPhone = bizPhoneErr;
  if (form.availability.length === 0)
    errs.availability = 'Select at least one service timeline';
  return errs;
}

/** Lets the loading overlay advance step-by-step even though enrollment is one API call. */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Minimum time on step 1 so a fast response does not skip “Creating your business profile”. */
const ENROLL_OVERLAY_STEP1_MIN_MS = 600;
/** Pause between overlay steps so React can paint each label before the next. */
const ENROLL_OVERLAY_STEP_PAUSE_MS = 500;

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const ProviderSignup = () => {
  const navigate = useNavigate();
  const { user, refreshUser, loading: authLoading } = useAuth();

  /**
   * Whether this session started already logged in (skips the "Your Info"
   * step). Locked the first time auth resolves and never re-evaluated after
   * that — otherwise a guest who just enrolled would flip from `false` to
   * `true` the instant `refreshUser()` populates `user`, reshaping
   * `stepperSteps`/`lastStepIndex` out from under a `step` index that was
   * just set based on the old shape (stranding the wizard on a blank step).
   */
  const [isLoggedInFlow, setIsLoggedInFlow] = useState(Boolean(user));
  const isLoggedInFlowLockedRef = useRef(false);
  useEffect(() => {
    if (!authLoading && !isLoggedInFlowLockedRef.current) {
      isLoggedInFlowLockedRef.current = true;
      setIsLoggedInFlow(Boolean(user));
    }
  }, [authLoading, user]);

  const stepperSteps = isLoggedInFlow
    ? [
        { key: 'business', label: 'Business' },
        { key: 'review', label: 'Review' },
        { key: 'agreement', label: 'Agreement' },
      ]
    : STEPS;
  const lastStepIndex = stepperSteps.length - 1;

  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /* ── Partnership Agreement (Step 4) ── */
  const [pdfReady, setPdfReady] = useState(false);
  const [agreementBusinessId, setAgreementBusinessId] = useState(null);
  const [agreementAcknowledged, setAgreementAcknowledged] = useState(false);
  const [agreementSubmitting, setAgreementSubmitting] = useState(false);
  const [agreementError, setAgreementError] = useState(null);
  const agreementPdfSrc = useMemo(() => buildPartnershipAgreementPdfSrc(), []);

  useEffect(() => {
    const url = agreementPdfSrc.split('#')[0];
    fetch(url, { method: 'HEAD' })
      .then((res) => {
        if (res.ok) setPdfReady(true);
      })
      .catch(() => {});
  }, [agreementPdfSrc]);

  /* ── Loading + submission error state ── */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submittingStep, setSubmittingStep] = useState(0); // 0=idle, 1=enrolling, 2=account sync UI, 3=session

  /* ── Conflict modals (409 from enroll / consumer fallback) ── */
  const [showDuplicateBusinessModal, setShowDuplicateBusinessModal] =
    useState(false);
  /** `{ message, code }` from API 409 or null */
  const [enrollmentAccountModal, setEnrollmentAccountModal] = useState(null);
  /** Logged-in user already has BusinessId — block duplicate enroll */
  const [linkedBusinessNotice, setLinkedBusinessNotice] = useState(null);

  /**
   * Logged-in user already has a BusinessId. If that business's Partnership
   * Agreement is unsigned (e.g. they were interrupted mid-signup and are
   * logging back in), resume the wizard on Step 4 instead of blocking them
   * with the "already linked" notice below. Only block when the agreement is
   * already signed, since that means they're trying to create a second
   * business.
   */
  useEffect(() => {
    if (authLoading || !user) {
      setLinkedBusinessNotice(null);
      return undefined;
    }
    // Already resolved this (either via a fresh enrollment just above, or a
    // previous run of this same check) — skip the redundant re-fetch.
    if (agreementBusinessId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const acct = await accountService.getAccount();
        if (cancelled || !acct?.BusinessId) {
          if (!cancelled) setLinkedBusinessNotice(null);
          return;
        }
        let biz = null;
        try {
          biz = await businessService.getBusiness(acct.BusinessId, false, 'owner');
        } catch {
          biz = null;
        }
        if (cancelled) return;

        if (biz?.PartnershipAgreementAcceptedAt) {
          const businessName =
            (biz?.BusinessName && String(biz.BusinessName).trim()) || '';
          setLinkedBusinessNotice({
            businessName: businessName || 'your linked business',
          });
          return;
        }

        setAgreementBusinessId(acct.BusinessId);
        setStep(lastStepIndex);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setLinkedBusinessNotice(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, lastStepIndex, agreementBusinessId]);

  const dismissLinkedBusinessNoticeGoHome = () => {
    setLinkedBusinessNotice(null);
    navigate('/');
  };

  const [form, setForm] = useState({
    /* ── Business fields ── */
    businessName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    businessEmail: '',
    businessPhone: '',
    businessWebsite: '',
    businessAbout: '',
    availability: [],

    /* ── User / account fields ── */
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    dateOfBirth: '',
    agreedToTerms: false,
  });

  /* ── Handlers ── */
  const handleChange = (field) => (e) => {
    const raw = e.target.value;
    const value =
      field === 'businessPhone' || field === 'phone'
        ? formatPhoneInputMask(raw)
        : raw;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleFormFieldBlur = (field) => (e) => {
    const signupFieldMap = {
      addressLine1: 'AddressLine1',
      addressLine2: 'AddressLine2',
      city: 'City',
      state: 'State',
      zip: 'ZipCode',
      businessEmail: 'BusinessEmail',
      businessWebsite: 'BusinessWebsiteUrl',
    };

    const raw = e.target.value;

    if (field === 'firstName' || field === 'lastName') {
      const normalized = toPersonNameCase(raw);
      if (normalized !== raw) {
        setForm((prev) => ({ ...prev, [field]: normalized }));
      }
      return;
    }

    const apiField = signupFieldMap[field];
    if (!apiField) return;

    const normalized = normalizeBusinessFormFieldValue(apiField, raw);
    if (normalized !== raw) {
      setForm((prev) => ({ ...prev, [field]: normalized }));
    }
  };

  const handleAgreedToTermsChange = (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({ ...prev, agreedToTerms: checked }));
    if (errors.agreedToTerms)
      setErrors((prev) => ({ ...prev, agreedToTerms: null }));
  };

  const toggleAvailability = (key) => {
    setForm((prev) => ({
      ...prev,
      availability: prev.availability.includes(key)
        ? prev.availability.filter((a) => a !== key)
        : [...prev.availability, key],
    }));
    if (errors.availability)
      setErrors((prev) => ({ ...prev, availability: null }));
  };

  /* ── Validation per step ── */
  const validateStep = useCallback(
    (currentStep) => {
      const errs = {};

      if (currentStep === 0) {
        Object.assign(errs, collectBusinessStepErrors(form));
      }

      if (currentStep === 1) {
        if (isLoggedInFlow) {
          if (!form.agreedToTerms)
            errs.agreedToTerms =
              'You must agree to the Terms of Service and Privacy Policy';
        } else {
          if (!form.firstName.trim()) errs.firstName = 'First name is required';
          if (!form.lastName.trim()) errs.lastName = 'Last name is required';
          const emailErr = getSignupEmailError(form.email, {
            requiredLabel: 'Email is required',
          });
          if (emailErr) errs.email = emailErr;
          const pwdMsg = getPasswordValidationError(form.password);
          if (pwdMsg) errs.password = pwdMsg;
          if (!form.confirmPassword)
            errs.confirmPassword = 'Please confirm your password';
          else if (form.password !== form.confirmPassword)
            errs.confirmPassword = 'Passwords do not match';
          const phoneErr = getSignupPhoneError(form.phone);
          if (phoneErr) errs.phone = phoneErr;
          if (!form.dateOfBirth) errs.dateOfBirth = 'Date of birth is required';
          if (!form.agreedToTerms)
            errs.agreedToTerms =
              'You must agree to the Terms of Service and Privacy Policy';
        }
      }

      setErrors(errs);
      return Object.keys(errs).length === 0;
    },
    [form, isLoggedInFlow],
  );

  const goNext = () => {
    if (validateStep(step)) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeEmail = () => {
    setEnrollmentAccountModal(null);
    setSubmitError(null);
    setStep(isLoggedInFlow ? 0 : 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToLogin = () => {
    setEnrollmentAccountModal(null);
    navigate('/login');
  };

  const closeEnrollmentAccountModal = () => setEnrollmentAccountModal(null);

  const buildBusinessEnrollmentPayload = () => {
    const address = normalizeBusinessAddress({
      AddressLine1: form.addressLine1,
      AddressLine2: form.addressLine2,
      City: form.city,
      State: form.state,
      ZipCode: form.zip,
    });

    return {
      BusinessName: form.businessName,
      BusinessAddress: {
        AddressLine1: address.AddressLine1,
        AddressLine2: address.AddressLine2,
        City: address.City,
        State: address.State,
        ZipCode: address.ZipCode,
      },
      BusinessEmail: normalizeBusinessEmail(form.businessEmail),
      BusinessPhoneNumber: phoneDigitsForApi(form.businessPhone),
      BusinessWebsiteUrl: normalizeBusinessWebsiteUrl(form.businessWebsite),
      BusinessAbout: form.businessAbout,
      Availability: form.availability,
      BusinessPublished: false,
    };
  };

  /** After 409 business_already_exists: create consumer account, notify ops; server sets session cookie like POST /enroll. */
  const handleConfirmConsumerOnlyAfterDuplicateBusiness = async () => {
    setShowDuplicateBusinessModal(false);
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmittingStep(1);
    try {
      if (
        !form.agreedToTerms ||
        getPasswordValidationError(form.password) ||
        getSignupEmailError(form.email, { requiredLabel: 'Email is required' }) ||
        getSignupPhoneError(form.phone)
      ) {
        validateStep(1);
        setStep(1);
        setSubmitError(
          'Please complete your account details and accept the terms.',
        );
        setSubmittingStep(0);
        setIsSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const businessPayload = buildBusinessEnrollmentPayload();
      const fallbackStarted = Date.now();
      await businessService.completeProviderConsumerFallback({
        business: businessPayload,
        account: {
          FirstName: toPersonNameCase(form.firstName),
          LastName: toPersonNameCase(form.lastName),
          Email: form.email,
          Password: form.password,
          PhoneNumber: phoneDigitsForApi(form.phone),
          DateOfBirth: form.dateOfBirth,
        },
      });
      await delay(
        Math.max(
          0,
          ENROLL_OVERLAY_STEP1_MIN_MS - (Date.now() - fallbackStarted),
        ),
      );
      setSubmittingStep(2);
      await delay(ENROLL_OVERLAY_STEP_PAUSE_MS);
      setSubmittingStep(3);
      const refreshResult = await refreshUser();
      if (refreshResult.success) {
        navigate('/');
      } else {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Consumer fallback enrollment failed:', error);
      if (isEnrollmentAccountConflict(error)) {
        setEnrollmentAccountModal({
          message:
            error.message ||
            'An account with this email already exists. Sign in or use a different email.',
          code: error.code,
        });
      } else {
        setSubmitError(
          error.message ||
            'Something went wrong creating your account. Please try again.',
        );
      }
    } finally {
      setIsSubmitting(false);
      setSubmittingStep(0);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SUBMIT — single POST /business-api/enroll (business + account + session cookie), then refreshUser
     ═══════════════════════════════════════════════════════════════════════ */
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmittingStep(1);

    try {
      if (isLoggedInFlow) {
        const mergedErrs = {
          ...collectBusinessStepErrors(form),
        };
        if (!form.agreedToTerms) {
          mergedErrs.agreedToTerms =
            'You must agree to the Terms of Service and Privacy Policy';
        }
        setErrors(mergedErrs);
        if (Object.keys(mergedErrs).length) {
          setStep(1);
          setSubmitError(
            'Please fix any issues above and accept the terms to submit.',
          );
          setSubmittingStep(0);
          setIsSubmitting(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        const businessPayload = buildBusinessEnrollmentPayload();
        const enrollAuthedStarted = Date.now();
        await businessService.completeAuthenticatedProviderEnrollment({
          business: businessPayload,
        });

        await delay(
          Math.max(
            0,
            ENROLL_OVERLAY_STEP1_MIN_MS - (Date.now() - enrollAuthedStarted),
          ),
        );
        setSubmittingStep(2);
        await delay(ENROLL_OVERLAY_STEP_PAUSE_MS);
        setSubmittingStep(3);
        const authedResult = await refreshUser();

        if (authedResult.success && authedResult.userData?.BusinessId) {
          setAgreementBusinessId(authedResult.userData.BusinessId);
          setStep(lastStepIndex);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          setSubmitted(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }

      if (
        !form.agreedToTerms ||
        getPasswordValidationError(form.password) ||
        getSignupEmailError(form.email, { requiredLabel: 'Email is required' }) ||
        getSignupPhoneError(form.phone)
      ) {
        validateStep(1);
        setStep(1);
        setSubmitError(
          'Please complete your account details and accept the terms.',
        );
        setSubmittingStep(0);
        setIsSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const businessPayload = buildBusinessEnrollmentPayload();

      const enrollStarted = Date.now();
      await businessService.completeProviderEnrollment({
        business: businessPayload,
        account: {
          FirstName: toPersonNameCase(form.firstName),
          LastName: toPersonNameCase(form.lastName),
          Email: form.email,
          Password: form.password,
          PhoneNumber: phoneDigitsForApi(form.phone),
          DateOfBirth: form.dateOfBirth,
        },
      });

      await delay(
        Math.max(0, ENROLL_OVERLAY_STEP1_MIN_MS - (Date.now() - enrollStarted)),
      );
      setSubmittingStep(2);
      await delay(ENROLL_OVERLAY_STEP_PAUSE_MS);
      setSubmittingStep(3);
      const result = await refreshUser();

      if (result.success && result.userData?.BusinessId) {
        setAgreementBusinessId(result.userData.BusinessId);
        setStep(lastStepIndex);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Enrollment failed:', error);

      if (error.status === 409 && error.code === 'business_already_exists') {
        setShowDuplicateBusinessModal(true);
      } else if (isEnrollmentAccountConflict(error)) {
        setEnrollmentAccountModal({
          message:
            error.message ||
            'Enrollment could not be completed with this email. Try signing in or use a different email.',
          code: error.code,
        });
      } else {
        setSubmitError(
          error.message ||
            'Something went wrong during enrollment. Please try again.',
        );
      }
    } finally {
      setIsSubmitting(false);
      setSubmittingStep(0);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     STEP 4 — Partnership Agreement acceptance
     ═══════════════════════════════════════════════════════════════════════ */
  const handleSignAgreement = async () => {
    if (!agreementAcknowledged || !agreementBusinessId || agreementSubmitting)
      return;
    setAgreementSubmitting(true);
    setAgreementError(null);
    try {
      await businessService.editBusiness(agreementBusinessId, {
        PartnershipAgreementAccepted: true,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Partnership agreement acceptance failed:', error);
      setAgreementError(
        error.message || 'Could not record your acceptance. Please try again.',
      );
    } finally {
      setAgreementSubmitting(false);
    }
  };

  /* ── Derived ── */
  const showReviewStep = isLoggedInFlow ? step === 1 : step === 2;
  const isAgreementStep = step === lastStepIndex;
  const pwStrength = getPasswordStrength(form.password);

  /* ══════════════════════════════════════
     RENDER — Success State
     ══════════════════════════════════════ */
  if (submitted) {
    return (
      <SignupPageWrapper>
        <HeroBanner>
          <HeroTitle>You're All Set!</HeroTitle>
          <HeroSubtitle>Your business is enrolled and the agreement is signed.</HeroSubtitle>
          <ProgressBarTrack>
            <ProgressBarFill $percent={100} />
          </ProgressBarTrack>
        </HeroBanner>

        <FormCard>
          <SuccessWrapper>
            <SuccessIcon>✓</SuccessIcon>
            <SuccessTitle>Welcome to NKY Connect!</SuccessTitle>
            <TrialBadge>🎉 {PRO_OFFER_SUMMARY}</TrialBadge>
            <SuccessMessage>
              Next, add the services you offer so customers can find you. If
              you'd rather not do it now, our team will set them up for you
              and reach out if you need a hand.
            </SuccessMessage>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 20,
              }}
            >
              <PrimaryButton
                type="button"
                onClick={() => navigate('/dashboard/service-catalog')}
              >
                Continue to Set Up Services →
              </PrimaryButton>
            </div>

            <FinePrint>
              No credit card required. Cancel anytime — no obligation.
            </FinePrint>
          </SuccessWrapper>
        </FormCard>
      </SignupPageWrapper>
    );
  }

  /* ══════════════════════════════════════
     RENDER — Step Flow
     ══════════════════════════════════════ */
  return (
    <SignupPageWrapper>
      {/* Stepper */}
      <StepperContainer>
        {stepperSteps.map((s, i) => (
          <React.Fragment key={s.key}>
            {i > 0 && <StepConnector $completed={i <= step} />}
            <StepIndicator>
              <StepCircle $active={i === step} $completed={i < step}>
                {i < step ? '✓' : i + 1}
              </StepCircle>
              <StepLabelText $active={i === step} $completed={i < step}>
                {s.label}
              </StepLabelText>
            </StepIndicator>
          </React.Fragment>
        ))}
      </StepperContainer>

      <FormCard key={step} $wide={isAgreementStep}>
        {/* ── Loading overlay ── */}
        {isSubmitting && (
          <LoadingOverlay>
            <NkySpinner size="lg" sx={{ mb: 3 }} />
            <LoadingTitle>Setting up your account…</LoadingTitle>
            <LoadingSteps>
              <LoadingStep $status={submittingStep > 1 ? 'done' : 'active'}>
                <LoadingStepIcon
                  $status={submittingStep > 1 ? 'done' : 'active'}
                >
                  {submittingStep > 1 ? '✓' : '1'}
                </LoadingStepIcon>
                Creating your business profile
              </LoadingStep>
              <LoadingStep
                $status={
                  submittingStep > 2
                    ? 'done'
                    : submittingStep === 2
                      ? 'active'
                      : 'pending'
                }
              >
                <LoadingStepIcon
                  $status={
                    submittingStep > 2
                      ? 'done'
                      : submittingStep === 2
                        ? 'active'
                        : 'pending'
                  }
                >
                  {submittingStep > 2 ? '✓' : '2'}
                </LoadingStepIcon>
                Setting up your account
              </LoadingStep>
              <LoadingStep
                $status={
                  submittingStep > 3
                    ? 'done'
                    : submittingStep === 3
                      ? 'active'
                      : 'pending'
                }
              >
                <LoadingStepIcon
                  $status={
                    submittingStep > 3
                      ? 'done'
                      : submittingStep === 3
                        ? 'active'
                        : 'pending'
                  }
                >
                  {submittingStep > 3 ? '✓' : '3'}
                </LoadingStepIcon>
                Signing you in
              </LoadingStep>
            </LoadingSteps>
          </LoadingOverlay>
        )}

        {/* ═══════════════════════════════════════
            STEP 1: Business Info
            ═══════════════════════════════════════ */}
        {step === 0 && (
          <>
            <StepTitle>Tell us about your business</StepTitle>
            <StepSubtitle>
              This information helps us create your NKY Connect business
              profile.
            </StepSubtitle>

            <FieldRow>
              <FieldGroup>
                <Label>Business Name *</Label>
                <Input
                  placeholder="e.g. Reliable Plumbing NKY"
                  value={form.businessName}
                  onChange={handleChange('businessName')}
                />
                {errors.businessName && (
                  <ErrorText>{errors.businessName}</ErrorText>
                )}
              </FieldGroup>
              <FieldGroup>
                <Label>Business Phone *</Label>
                <Input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="555-123-4567"
                  value={form.businessPhone}
                  onChange={handleChange('businessPhone')}
                />
                {errors.businessPhone && (
                  <ErrorText>{errors.businessPhone}</ErrorText>
                )}
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <Label>Address Line 1 *</Label>
                <Input
                  placeholder="123 Main Street"
                  value={form.addressLine1}
                  onChange={handleChange('addressLine1')}
                  onBlur={handleFormFieldBlur('addressLine1')}
                />
                {errors.addressLine1 && (
                  <ErrorText>{errors.addressLine1}</ErrorText>
                )}
              </FieldGroup>
              <FieldGroup>
                <Label>Address Line 2</Label>
                <Input
                  placeholder="Suite, Unit, Bldg (optional)"
                  value={form.addressLine2}
                  onChange={handleChange('addressLine2')}
                  onBlur={handleFormFieldBlur('addressLine2')}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <Label>City *</Label>
                <Input
                  placeholder="Fort Thomas"
                  value={form.city}
                  onChange={handleChange('city')}
                  onBlur={handleFormFieldBlur('city')}
                />
                {errors.city && <ErrorText>{errors.city}</ErrorText>}
              </FieldGroup>
              <FieldGroup>
                <Label>State *</Label>
                <Select value={form.state} onChange={handleChange('state')}>
                  <option value="">Select…</option>
                  {SERVICE_AREA_STATE_CODES.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </Select>
                {errors.state && <ErrorText>{errors.state}</ErrorText>}
              </FieldGroup>
              <FieldGroup>
                <Label>ZIP *</Label>
                <Input
                  placeholder="41075"
                  value={form.zip}
                  onChange={handleChange('zip')}
                  onBlur={handleFormFieldBlur('zip')}
                  maxLength={5}
                />
                {errors.zip && <ErrorText>{errors.zip}</ErrorText>}
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <Label>Business Email *</Label>
                <Input
                  type="email"
                  placeholder="contact@yourbusiness.com"
                  value={form.businessEmail}
                  onChange={handleChange('businessEmail')}
                  onBlur={handleFormFieldBlur('businessEmail')}
                />
                {errors.businessEmail && (
                  <ErrorText>{errors.businessEmail}</ErrorText>
                )}
              </FieldGroup>
              <FieldGroup>
                <Label>Website (optional)</Label>
                <Input
                  placeholder="https://www.yourbusiness.com"
                  value={form.businessWebsite}
                  onChange={handleChange('businessWebsite')}
                  onBlur={handleFormFieldBlur('businessWebsite')}
                />
              </FieldGroup>
            </FieldRow>

            <FieldGroup>
              <Label>About Your Business (optional)</Label>
              <Textarea
                placeholder="Tell potential customers what makes your business stand out…"
                value={form.businessAbout}
                onChange={handleChange('businessAbout')}
                rows={3}
              />
            </FieldGroup>

            <SectionDivider />

            <FieldGroup>
              <Label>
                Which service timelines does your business support? *
              </Label>
              <StepSubtitle style={{ margin: '0 0 12px', fontSize: '0.82rem' }}>
                Select all that apply — this helps us match you with the right
                customers.
              </StepSubtitle>
              <AvailabilityGrid>
                {AVAILABILITY_OPTIONS.map((opt) => {
                  const selected = form.availability.includes(opt.key);
                  return (
                    <AvailabilityChip
                      key={opt.key}
                      type="button"
                      $selected={selected}
                      onClick={() => toggleAvailability(opt.key)}
                    >
                      <AvailabilityChipIcon>{opt.icon}</AvailabilityChipIcon>
                      <div>
                        <AvailabilityChipLabel $selected={selected}>
                          {opt.label}
                        </AvailabilityChipLabel>
                        <br />
                        <AvailabilityChipDesc>{opt.desc}</AvailabilityChipDesc>
                      </div>
                    </AvailabilityChip>
                  );
                })}
              </AvailabilityGrid>
              {errors.availability && (
                <ErrorText>{errors.availability}</ErrorText>
              )}
            </FieldGroup>
          </>
        )}

        {/* ═══════════════════════════════════════
            STEP 2: User / Account Info (guest only)
            ═══════════════════════════════════════ */}
        {!isLoggedInFlow && step === 1 && (
          <>
            <StepTitle>Create your account</StepTitle>
            <StepSubtitle>
              This is your personal login for managing your business on NKY
              Connect.
            </StepSubtitle>

            <FieldRow>
              <FieldGroup>
                <Label>First Name *</Label>
                <Input
                  placeholder="John"
                  value={form.firstName}
                  onChange={handleChange('firstName')}
                  onBlur={handleFormFieldBlur('firstName')}
                />
                {errors.firstName && <ErrorText>{errors.firstName}</ErrorText>}
              </FieldGroup>
              <FieldGroup>
                <Label>Last Name *</Label>
                <Input
                  placeholder="Doe"
                  value={form.lastName}
                  onChange={handleChange('lastName')}
                  onBlur={handleFormFieldBlur('lastName')}
                />
                {errors.lastName && <ErrorText>{errors.lastName}</ErrorText>}
              </FieldGroup>
            </FieldRow>

            <FieldGroup>
              <Label>Email (this will be your username) *</Label>
              <Input
                type="email"
                placeholder="you@email.com"
                value={form.email}
                onChange={handleChange('email')}
              />
              {errors.email && <ErrorText>{errors.email}</ErrorText>}
            </FieldGroup>

            <FieldGroup>
              <Label>Password *</Label>
              <PasswordWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="8+ chars: upper, lower, number, symbol"
                  value={form.password}
                  onChange={handleChange('password')}
                  style={{ paddingRight: '54px' }}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <VisibilityOff sx={{ fontSize: 22 }} />
                  ) : (
                    <Visibility sx={{ fontSize: 22 }} />
                  )}
                </PasswordToggle>
              </PasswordWrapper>
              {form.password && (
                <PasswordStrength>
                  <PasswordStrengthBar
                    $score={pwStrength.score}
                    $maxScore={pwStrength.maxScore}
                    $color={pwStrength.color}
                  />
                  <PasswordStrengthLabel $color={pwStrength.color}>
                    {pwStrength.label}
                  </PasswordStrengthLabel>
                </PasswordStrength>
              )}
              {errors.password && <ErrorText>{errors.password}</ErrorText>}
            </FieldGroup>

            <FieldGroup>
              <Label>Confirm Password *</Label>
              <PasswordWrapper>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={form.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  style={{ paddingRight: '54px' }}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={
                    showConfirmPassword
                      ? 'Hide confirm password'
                      : 'Show confirm password'
                  }
                >
                  {showConfirmPassword ? (
                    <VisibilityOff sx={{ fontSize: 22 }} />
                  ) : (
                    <Visibility sx={{ fontSize: 22 }} />
                  )}
                </PasswordToggle>
              </PasswordWrapper>
              {errors.confirmPassword && (
                <ErrorText>{errors.confirmPassword}</ErrorText>
              )}
              {form.confirmPassword.length > 0 &&
                form.password === form.confirmPassword &&
                !errors.confirmPassword && (
                  <ConfirmPasswordMatch role="status" aria-live="polite">
                    <CheckCircleIcon
                      sx={{ fontSize: 18, color: 'inherit', flexShrink: 0 }}
                      aria-hidden
                    />
                    Your passwords match.
                  </ConfirmPasswordMatch>
                )}
            </FieldGroup>

            <SectionDivider />

            <FieldRow>
              <FieldGroup>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="555-123-4567"
                  value={form.phone}
                  onChange={handleChange('phone')}
                />
                {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
              </FieldGroup>
              <FieldGroup>
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={form.dateOfBirth}
                  onChange={handleChange('dateOfBirth')}
                />
                {errors.dateOfBirth && (
                  <ErrorText>{errors.dateOfBirth}</ErrorText>
                )}
              </FieldGroup>
            </FieldRow>

            <FieldGroup>
              <FormControlLabel
                sx={{
                  alignItems: 'center',
                  marginLeft: 0,
                  gap: '6px',
                  '& .MuiFormControlLabel-label': {
                    display: 'inline',
                    lineHeight: 1.45,
                  },
                }}
                control={
                  <Checkbox
                    checked={form.agreedToTerms}
                    onChange={handleAgreedToTermsChange}
                    name="agreedToTerms"
                    size="small"
                    sx={{ p: '4px' }}
                  />
                }
                label={
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ display: 'inline', lineHeight: 1.45, color: '#555' }}
                  >
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Link>
                    <Typography
                      component="span"
                      sx={{ color: 'error.main', fontWeight: 700 }}
                      aria-hidden
                    >
                      {' '}
                      *
                    </Typography>
                  </Typography>
                }
              />
              {errors.agreedToTerms && (
                <ErrorText>{errors.agreedToTerms}</ErrorText>
              )}
            </FieldGroup>
          </>
        )}

        {/* ═══════════════════════════════════════
            STEP 3: Review & Submit (guest step 2; signed-in step 1)
            ═══════════════════════════════════════ */}
        {showReviewStep && (
          <>
            <StepTitle>Review & Submit</StepTitle>
            <StepSubtitle>
              You're almost there. Review the details below, then submit —
              you'll just need to sign the partnership agreement to activate
              your listing.
            </StepSubtitle>

            <SectionLabel>Business</SectionLabel>
            <SummaryGrid>
              <SummaryRow>
                <SummaryLabel>Name</SummaryLabel>
                <SummaryValue>{form.businessName}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Address</SummaryLabel>
                <SummaryValue>
                  {form.addressLine1}
                  {form.addressLine2 ? `, ${form.addressLine2}` : ''}
                  <br />
                  {form.city}, {form.state} {form.zip}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Email</SummaryLabel>
                <SummaryValue>{form.businessEmail}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Phone</SummaryLabel>
                <SummaryValue>{form.businessPhone}</SummaryValue>
              </SummaryRow>
              {form.businessWebsite && (
                <SummaryRow>
                  <SummaryLabel>Website</SummaryLabel>
                  <SummaryValue>{form.businessWebsite}</SummaryValue>
                </SummaryRow>
              )}
              <SummaryRow $alignStart>
                <SummaryLabel>Service Timelines</SummaryLabel>
                <ReviewTimelinesList>
                  {AVAILABILITY_OPTIONS.map((opt) => {
                    const checked = form.availability.includes(opt.key);
                    return (
                      <ReviewTimelineRow key={opt.key}>
                        <ReviewTimelineCheckboxCell>
                          <Checkbox
                            checked={checked}
                            disabled
                            disableRipple
                            size="small"
                            tabIndex={-1}
                            sx={{
                              p: '6px',
                              color: '#c8cdd8',
                              '&.Mui-checked': { color: '#01ebb2' },
                              '&.Mui-disabled': { color: '#d5d8e0' },
                              '&.Mui-disabled.Mui-checked': {
                                color: '#01ebb2',
                              },
                            }}
                            inputProps={{
                              'aria-label': `${opt.label}, ${checked ? 'selected' : 'not selected'}`,
                            }}
                          />
                        </ReviewTimelineCheckboxCell>
                        <ReviewTimelineText>
                          <ReviewTimelineTitle>
                            <span aria-hidden="true">{opt.icon} </span>
                            {opt.label}
                          </ReviewTimelineTitle>
                          <ReviewTimelineDesc>{opt.desc}</ReviewTimelineDesc>
                        </ReviewTimelineText>
                      </ReviewTimelineRow>
                    );
                  })}
                </ReviewTimelinesList>
              </SummaryRow>
            </SummaryGrid>

            <SectionLabel>Account Owner</SectionLabel>
            <SummaryGrid>
              <SummaryRow>
                <SummaryLabel>Name</SummaryLabel>
                <SummaryValue>
                  {isLoggedInFlow
                    ? [user.FirstName, user.LastName].filter(Boolean).join(' ') ||
                      '—'
                    : `${form.firstName} ${form.lastName}`}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Login Email</SummaryLabel>
                <SummaryValue>
                  {isLoggedInFlow ? user.Email || '—' : form.email}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Phone</SummaryLabel>
                <SummaryValue>
                  {isLoggedInFlow
                    ? formatPhoneInputMask(user.PhoneNumber || '') || '—'
                    : form.phone}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Date of Birth</SummaryLabel>
                <SummaryValue>
                  {isLoggedInFlow
                    ? user.DateOfBirth
                      ? new Date(
                          `${user.DateOfBirth}T00:00:00`,
                        ).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'
                    : form.dateOfBirth
                      ? new Date(
                          form.dateOfBirth + 'T00:00:00',
                        ).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'}
                </SummaryValue>
              </SummaryRow>
            </SummaryGrid>

            {isLoggedInFlow && (
              <FieldGroup style={{ marginTop: 16, marginBottom: 8 }}>
                <FormControlLabel
                  sx={{
                    alignItems: 'center',
                    marginLeft: 0,
                    gap: '6px',
                    '& .MuiFormControlLabel-label': {
                      display: 'inline',
                      lineHeight: 1.45,
                    },
                  }}
                  control={
                    <Checkbox
                      checked={form.agreedToTerms}
                      onChange={handleAgreedToTermsChange}
                      name="agreedToTerms"
                      size="small"
                      sx={{ p: '4px' }}
                    />
                  }
                  label={
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ display: 'inline', lineHeight: 1.45, color: '#555' }}
                    >
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </Link>
                      <Typography
                        component="span"
                        sx={{ color: 'error.main', fontWeight: 700 }}
                        aria-hidden
                      >
                        {' '}
                        *
                      </Typography>
                    </Typography>
                  }
                />
                {errors.agreedToTerms && (
                  <ErrorText>{errors.agreedToTerms}</ErrorText>
                )}
              </FieldGroup>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 4,
              }}
            >
              <TrialBadge>🎉 {PRO_OFFER_SUMMARY}</TrialBadge>
            </div>

            <FinePrint>
              No credit card required now. Cancel anytime — no obligation.
            </FinePrint>

            {/* ── Submission error banner ── */}
            {submitError && (
              <ErrorText
                style={{
                  textAlign: 'center',
                  marginTop: '12px',
                  fontSize: '0.9rem',
                }}
              >
                {submitError}
              </ErrorText>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════
            STEP 4: Partnership Agreement (mandatory)
            ═══════════════════════════════════════ */}
        {isAgreementStep && (
          <>
            <StepTitle>Sign the Partnership Agreement</StepTitle>
            <StepSubtitle>
              Your account and business are set up. Read the agreement below,
              then confirm your signature at the bottom — this is required
              before you can continue.
            </StepSubtitle>

            {!pdfReady ? (
              <StepSubtitle style={{ margin: '0 0 20px' }}>
                Loading document…
              </StepSubtitle>
            ) : (
              <div
                style={{
                  border: '1.5px solid #e0e4ec',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  marginBottom: 12,
                }}
              >
                <iframe
                  src={agreementPdfSrc}
                  title="Business Partnership Agreement"
                  style={{
                    width: '100%',
                    height: '78vh',
                    minHeight: '520px',
                    border: 'none',
                    display: 'block',
                    background: '#fff',
                  }}
                />
              </div>
            )}

            <Typography variant="body2" sx={{ mb: 3, color: '#555' }}>
              If the document does not load,{' '}
              <a
                href={agreementPdfSrc.split('#')[0]}
                target="_blank"
                rel="noopener noreferrer"
              >
                open it in a new tab
              </a>
              .
            </Typography>

            <AgreementSignatureCard>
              <AgreementSignatureHeading>
                <span aria-hidden="true">✍️</span> Confirm your signature
              </AgreementSignatureHeading>

              <FormControlLabel
                sx={{
                  alignItems: 'center',
                  marginLeft: 0,
                  gap: '6px',
                  '& .MuiFormControlLabel-label': {
                    display: 'inline',
                    lineHeight: 1.45,
                  },
                }}
                control={
                  <Checkbox
                    checked={agreementAcknowledged}
                    onChange={(e) => setAgreementAcknowledged(e.target.checked)}
                    name="agreementAcknowledged"
                    size="small"
                    sx={{ p: '4px' }}
                  />
                }
                label={
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ display: 'inline', lineHeight: 1.45, color: '#1a1a2e', fontWeight: 600 }}
                  >
                    I have read and agree to the NKY Connect Business
                    Partnership Agreement on behalf of my business.
                  </Typography>
                }
              />

              <AgreementSignatureHint>
                Checking this box and continuing records your signature and
                the date, and lets NKY Connect publish your storefront after
                review.
              </AgreementSignatureHint>

              {agreementError && (
                <ErrorText style={{ marginBottom: '14px', fontSize: '0.85rem' }}>
                  {agreementError}
                </ErrorText>
              )}

              <PrimaryButton
                type="button"
                onClick={handleSignAgreement}
                disabled={!agreementAcknowledged || agreementSubmitting}
              >
                {agreementSubmitting ? 'Signing…' : 'Sign & Continue'}
              </PrimaryButton>
            </AgreementSignatureCard>
          </>
        )}

        {/* ─── Navigation ─── */}
        {!isAgreementStep && (
          <ButtonRow>
            {step > 0 ? (
              <SecondaryButton
                type="button"
                onClick={goBack}
                disabled={isSubmitting}
              >
                ← Back
              </SecondaryButton>
            ) : (
              <div />
            )}

            {showReviewStep ? (
              <PrimaryButton
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting…' : 'Submit Enrollment 🚀'}
              </PrimaryButton>
            ) : (
              <PrimaryButton type="button" onClick={goNext}>
                {step === 0 && isLoggedInFlow
                  ? 'Review →'
                  : step === 0
                    ? 'Next: Your Info →'
                    : 'Review →'}
              </PrimaryButton>
            )}
          </ButtonRow>
        )}
      </FormCard>

      {/* ── Business name already exists: offer consumer-only signup ── */}
      {showDuplicateBusinessModal && (
        <ModalBackdrop onClick={() => setShowDuplicateBusinessModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalIcon>🏢</ModalIcon>
            <ModalTitle>Business Already Listed</ModalTitle>
            {isLoggedInFlow ? (
              <>
                <ModalBody>
                  A business named <strong>{form.businessName}</strong> is
                  already on NKY Connect. To join that team instead of creating a
                  duplicate listing, use the request form to link your account.
                </ModalBody>
                <ModalActions>
                  <ModalPrimaryButton
                    type="button"
                    onClick={() => {
                      setShowDuplicateBusinessModal(false);
                      navigate('/join-existing-business');
                    }}
                  >
                    Join an existing business
                  </ModalPrimaryButton>
                  <ModalSecondaryButton
                    type="button"
                    onClick={() => setShowDuplicateBusinessModal(false)}
                  >
                    Go back
                  </ModalSecondaryButton>
                </ModalActions>
              </>
            ) : (
              <>
                <ModalBody>
                  A business named <strong>{form.businessName}</strong> is already
                  on NKY Connect. You can still create a personal account without
                  linking a new business. We&apos;ll email our team with your
                  details so they can get you added as a business user.
                </ModalBody>
                <ModalActions>
                  <ModalPrimaryButton
                    type="button"
                    onClick={handleConfirmConsumerOnlyAfterDuplicateBusiness}
                    disabled={isSubmitting}
                  >
                    Yes, create my account
                  </ModalPrimaryButton>
                  <ModalSecondaryButton
                    type="button"
                    onClick={() => setShowDuplicateBusinessModal(false)}
                  >
                    No, go back
                  </ModalSecondaryButton>
                </ModalActions>
              </>
            )}
          </ModalCard>
        </ModalBackdrop>
      )}

      {/* ── Account / enrollment blocked (409 + detailed API message) ── */}
      {enrollmentAccountModal && (
        <ModalBackdrop onClick={closeEnrollmentAccountModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalIcon>✉️</ModalIcon>
            <ModalTitle>
              {enrollmentAccountConflictTitle(enrollmentAccountModal.code)}
            </ModalTitle>
            <ModalBody>
              {enrollmentAccountModal.code ===
              'enroll_account_linked_other_business' ? (
                <>
                  {enrollmentAccountModal.message}{' '}
                  <Link
                    to="/contact"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      color: '#0d4efe',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                    }}
                  >
                    Contact us
                  </Link>{' '}
                  if you need both profiles consolidated.
                </>
              ) : (
                enrollmentAccountModal.message
              )}
            </ModalBody>
            <ModalActions>
              <ModalPrimaryButton type="button" onClick={goToLogin}>
                Go to sign in
              </ModalPrimaryButton>
              <ModalSecondaryButton type="button" onClick={handleChangeEmail}>
                Change email
              </ModalSecondaryButton>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}

      {linkedBusinessNotice && (
        <ModalBackdrop onClick={dismissLinkedBusinessNoticeGoHome}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalIcon>🏢</ModalIcon>
            <ModalTitle>You already have a business linked</ModalTitle>
            <ModalBody>
              Your account is linked to{' '}
              <strong>{linkedBusinessNotice.businessName}</strong>. Open your
              business dashboard to manage that profile. Provider enrollment is
              for creating a <strong>new</strong> business listing.
            </ModalBody>
            <ModalBody style={{ marginTop: '-12px' }}>
              If this isn&apos;t correct, please{' '}
              <a
                href={teamContactMailtoHref({
                  subject: 'Incorrect business linked to my NKY Connect account',
                  body:
                    'Hi NKY Connect team,\n\nMy account shows the wrong business linked. Please help.\n\nThank you\n',
                })}
                style={{
                  color: '#0d4efe',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
              >
                contact us by email
              </a>
              .
            </ModalBody>
            <ModalActions>
              <ModalPrimaryButton
                type="button"
                onClick={() => {
                  setLinkedBusinessNotice(null);
                  navigate('/dashboard');
                }}
              >
                Go to business dashboard
              </ModalPrimaryButton>
              <ModalSecondaryButton
                type="button"
                onClick={dismissLinkedBusinessNoticeGoHome}
              >
                Back to home
              </ModalSecondaryButton>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}
    </SignupPageWrapper>
  );
};

export default ProviderSignup;
